import { csrfToken } from '../account/csrf';
import type { components } from '../multiplayer/api.generated';
import { forgetLegacyFinds, legacyFinds } from './journal';

type Kept = components['schemas']['Discovery'];
type Storage = Parameters<typeof legacyFinds>[1];

const PATH = '/api/discoveries/';
/** A finding the server did not take is offered again after this long. */
export const RETRY_MS = 10_000;

function isKept(value: unknown): value is Kept {
  return (
    typeof value === 'object' && value !== null && typeof Reflect.get(value, 'place') === 'string'
  );
}

function first(places: ReadonlySet<string>): string | undefined {
  return places.values().next().value;
}

function record(place: string): Promise<Response> {
  const token = csrfToken();
  return fetch(PATH, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'X-CSRFToken': token } : {}),
    },
    body: JSON.stringify({ place }),
  });
}

/** The discovery book of the signed-in account. The server keeps it; this holds what it said,
 *  and what was found since and is still on its way there. A browser that kept finds of its
 *  own before accounts existed hands them to the first account signed in on it. */
export class KeptBook {
  private readonly found = new Set<string>();
  private readonly unsaved = new Set<string>();
  private sending = false;
  private loaded = false;
  private failed = false;
  private retryAt = 0;
  /** Bumped on every load and sign-out, so an answer for an earlier account is dropped. */
  private generation = 0;

  constructor(
    private readonly known: readonly string[],
    private readonly storage: Storage,
    private readonly signedOut: () => void,
    private readonly changed: () => void
  ) {}

  /** Signed in, with the account's book read from the server. */
  get open(): boolean {
    return this.loaded;
  }

  /** A finding, or the book itself, could not be fetched or saved and waits for the next try. */
  get failing(): boolean {
    return this.failed;
  }

  get size(): number {
    return this.found.size;
  }

  has(id: string): boolean {
    return this.found.has(id);
  }

  /** Whether the place was new to the book. */
  add(id: string): boolean {
    if (!this.loaded || this.found.has(id) || !this.known.includes(id)) return false;
    this.found.add(id);
    this.unsaved.add(id);
    void this.send();
    return true;
  }

  /** Read the signed-in account's book. */
  async load(): Promise<void> {
    const current = ++this.generation;
    this.sending = false;
    try {
      const response = await fetch(PATH, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (current !== this.generation || this.refused(response.status)) return;
      if (!response.ok) throw new Error(`Discovery book not read: ${String(response.status)}`);
      const rows: unknown = await response.json();
      if (current !== this.generation) return;
      this.fill(Array.isArray(rows) ? rows : []);
      if (this.unsaved.size > 0) await this.send();
      else forgetLegacyFinds(this.storage);
    } catch {
      if (current === this.generation) this.fail();
    }
  }

  /** Signed out: the book is the account's, and leaves with it. */
  close(): void {
    this.generation += 1;
    this.found.clear();
    this.unsaved.clear();
    this.loaded = this.failed = this.sending = false;
    this.changed();
  }

  update(now: number): void {
    if (!this.failed || now < this.retryAt) return;
    this.retryAt = now + RETRY_MS;
    void (this.loaded ? this.send() : this.load());
  }

  private fill(rows: readonly unknown[]): void {
    this.found.clear();
    this.unsaved.clear();
    for (const row of rows)
      if (isKept(row) && this.known.includes(row.place)) this.found.add(row.place);
    for (const place of legacyFinds(this.known, this.storage))
      if (!this.found.has(place)) {
        this.found.add(place);
        this.unsaved.add(place);
      }
    this.loaded = true;
    this.failed = false;
    this.changed();
  }

  private async send(): Promise<void> {
    if (this.sending || this.unsaved.size === 0) return;
    this.sending = true;
    const current = this.generation;
    try {
      /* Places found while one is on its way join the same queue, so drain it to the end. */
      for (let place = first(this.unsaved); place; place = first(this.unsaved)) {
        const response = await record(place);
        if (current !== this.generation || this.refused(response.status)) return;
        if (!response.ok) throw new Error(`Discovery not saved: ${String(response.status)}`);
        this.unsaved.delete(place);
      }
      this.failed = false;
      forgetLegacyFinds(this.storage);
    } catch {
      if (current === this.generation) this.fail();
    } finally {
      if (current === this.generation) {
        this.sending = false;
        this.changed();
      }
    }
  }

  private fail(): void {
    this.failed = true;
    this.retryAt = performance.now() + RETRY_MS;
    this.changed();
  }

  /** The server no longer knows this visitor: the account decides what follows. */
  private refused(status: number): boolean {
    if (status !== 401 && status !== 403) return false;
    this.signedOut();
    return true;
  }
}
